import {
  CognitoAuth,
  CognitoAuthSession,
  CognitoToken,
} from 'amazon-cognito-auth-ts';
import { authConfig } from './config';
import { PermissionsGroups } from '../interfaces';
import { Dispatch } from './context';

let authInstance: CognitoAuth | null = null;
export const getAuth = (): CognitoAuth => {
  if (!authInstance) {
    authInstance = new CognitoAuth(authConfig());
    authInstance.useCodeGrantFlow();
  }
  return authInstance;
};

export const removeQueryFromLocation = (): void => {
  if (window.history.length > 0) {
    const newHref = window.location.href.split('?')[0];
    window.history.replaceState(undefined, 'Circe', newHref);
  }
};

const redirectToOriginalHash = (): void => {
  if (sessionStorage.getItem('startingUrlHash')) {
    const originalHash = sessionStorage.getItem('startingUrlHash');
    window.location.replace(
      window.location.href.substring(0, window.location.href.indexOf('#')) +
        (originalHash ?? '')
    );
  }
};
const nextRefresh = () =>
  new Date(+new Date() + refreshDelay).toLocaleTimeString();
const refreshDelay = 1000 * 60 * 30; // 1000ms = 1s, 60s = 1m, 30m (refresh every 30 min for 60 min token expiry)
export const ensureAuthenticated = (
  dispatch?: Dispatch
): Promise<CognitoAuthSession> =>
  new Promise((resolve, reject) => {
    const auth = getAuth();
    auth.userhandler = {
      onFailure: (err) => {
        auth.clearCachedTokensScopes();
        removeQueryFromLocation();
        reject(err);
      },
      onSuccess: (session) => {
        removeQueryFromLocation();
        resolve(auth.getSignInUserSession());
        setInterval(() => {
          console.info('Refreshing auth token');
          auth
            .refreshSession(session.refreshToken.refreshToken)
            .then((ses) => {
              if (ses.isValid()) {
                if (dispatch) {
                  const userInfo = extractUserInfoFromToken(ses.getIdToken());
                  dispatch({
                    type: 'LOGIN',
                    payload: {
                      isLoading: false,
                      isAuthenticated: true,
                      ...userInfo,
                    },
                  });
                  console.info(
                    'Auth token refreshed. Next refresh at',
                    nextRefresh()
                  );
                }
              } else {
                console.error('Auth token failed to refresh. Current state:');
              }
            })
            .catch((err) =>
              console.error('Auth token refresh request failed', err)
            );
        }, refreshDelay);
        console.info(
          'Authenticated with Cognito and will refresh token at',
          nextRefresh()
        );
      },
    };

    const { href } = window.location;
    let session;
    try {
      session = auth.getSignInUserSession();
    } catch (ex) {
      auth.clearCachedTokensScopes();
      session = auth.getSignInUserSession();
    }

    if (session.isValid()) {
      resolve(session);
    } else if (
      href.indexOf('?') > 0 &&
      href.substring(href.indexOf('?') + 1, href.indexOf('?') + 5) === 'code'
    ) {
      resolve(auth.parseCognitoWebResponse(href));
    } else {
      resolve(auth.getSession());
    }
  });

type AuthRes =
  | {
      isAuthenticated: boolean;
      token: string;
      userAlias?: string;
      userGroups?: string[];
      packageInfoPermissionsGroup: PermissionsGroups;
    }
  | { error: string };
type SessionToken = {
  identities: { userId: string }[];
  'custom:Groups': string;
};

export async function performUserAuthentication(
  dispatch?: Dispatch
): Promise<AuthRes> {
  try {
    const session = await ensureAuthenticated(dispatch);
    const userInfo = extractUserInfoFromToken(session.getIdToken());

    redirectToOriginalHash();

    return {
      isAuthenticated: !!userInfo.token,
      ...userInfo,
    };
  } catch (error) {
    console.log('there was an authentication error');
    console.log(error);
    return { error: String(error) };
  }
}

// Helper function to extract user information from token payload
const extractUserInfoFromToken = (idToken: CognitoToken) => {
  const payload = idToken.decodePayload() as SessionToken;
  const identity =
    payload.identities && payload.identities.length > 0
      ? payload.identities[0]
      : undefined;
  const userAlias = identity ? identity['userId'] : undefined;
  const token = idToken.getJwtToken();
  const userGroups =
    payload && Object.keys(payload).length
      ? (JSON.parse(payload['custom:Groups']) as string[])
      : undefined;

  let packageInfoPermissionsGroup: PermissionsGroups;
  if (userGroups?.includes('info-editor')) {
    packageInfoPermissionsGroup = 'info-editor';
  } else {
    packageInfoPermissionsGroup = 'readonly-user';
  }

  return {
    token,
    userAlias,
    userGroups,
    packageInfoPermissionsGroup,
  };
};

export function isTokenValidOrRefreshable(): boolean {
  const auth = getAuth();
  try {
    const session = auth.getSignInUserSession();

    if (!session) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiration = session.getAccessToken().getExpiration();

    if (currentTime < tokenExpiration) {
      return true;
    }

    if (!session.refreshToken) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('Session check failed:', error);
    return false;
  }
}

export async function getValidToken(): Promise<string | null> {
  const auth = getAuth();
  try {
    const session = auth.getSignInUserSession();
    if (!session) return null;

    const currentTime = Math.floor(Date.now() / 1000);
    const tokenExpiration = session.getAccessToken().getExpiration();
    const bufferTime = 300; // 5 minutes buffer

    // If token expires in less than 5 minutes, refresh it
    if (currentTime + bufferTime >= tokenExpiration) {
      console.log('Token expiring soon, refreshing...');
      const refreshedSession = await auth.refreshSession(
        session.getRefreshToken().getToken()
      );
      return refreshedSession.getIdToken().getJwtToken();
    }

    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error('Failed to get valid token:', error);
    return null;
  }
}
